import { useCallback } from 'react';
import { useField } from 'formik';
import {
  BaseColour,
  HeyLightColour,
} from '@heidi-pay/heidi-component-library/enums';
import {
  FormControl,
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
  OutlinedInput,
  SelectChangeEvent,
  styled,
} from '@mui/material';
import { DarkGreyOutline, FormGroup, Label } from '@components/Common.styles';
import { Tooltip } from '@components/Tooltip';
import { ITooltipContent } from '@components/types.d';
import { ValidationError } from '@components/ValidationError';

interface IDropdownOptions {
  id: string;
  name: string;
}

interface IMultiSelectDropdownProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  name: string;
  options: IDropdownOptions[];
  tooltip?: ITooltipContent;
}

const StyledMenuItem = styled(MenuItem)({
  paddingTop: '3px',
  paddingBottom: '3px',
  paddingLeft: '12px',
  paddingRight: '12px',
  '&.Mui-selected': {
    backgroundColor: BaseColour.Grey4,
  },
  '&.Mui-selected:hover': {
    backgroundColor: BaseColour.Grey5,
  },
});

const StyledCheckbox = styled(Checkbox)({
  padding: '0 8px 0 0',
  '&.Mui-checked': {
    color: HeyLightColour.BrandAccentsHeyLightRed,
  },
});

const StyledFormControl = styled(FormControl)({
  display: 'flex',
  flexDirection: 'column',
});

export const MultiSelectDropdown = ({
  label,
  name,
  options,
  tooltip,
}: IMultiSelectDropdownProps) => {
  const [field, meta, helpers] = useField<string[]>({ name });
  const { value } = field;
  const { setValue } = helpers;
  const error = meta.touched && meta.error;

  const getOptionNameById = useCallback(
    (id: string): string => options.find(opt => opt.id === id)?.name ?? id,
    [options],
  );

  const renderSelectedValue = useCallback(
    (selected: string[]) =>
      selected
        .map(getOptionNameById)
        .sort((a, b) => a.localeCompare(b))
        .join(','),
    [getOptionNameById],
  );

  const handleChange = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const {
        target: { value: selectedOptions },
      } = event;
      setValue(
        typeof selectedOptions === 'string'
          ? selectedOptions.split(',')
          : selectedOptions,
      );
    },
    [setValue],
  );

  return (
    <FormGroup>
      {tooltip && <Tooltip tip={tooltip} />}
      <Label htmlFor={name}>{label}</Label>
      <StyledFormControl error={!!error}>
        <Select
          input={<OutlinedInput label=" " notched={false} />}
          MenuProps={{
            PaperProps: {
              sx: {
                borderRadius: '4px',
                boxShadow: 'none',
                border: `1px solid ${DarkGreyOutline}`,
              },
            },
            MenuListProps: {
              sx: { padding: 0 },
            },
          }}
          multiple={true}
          onChange={handleChange}
          renderValue={renderSelectedValue}
          sx={{
            height: '29px',
            fontSize: '0.9rem',
            width: '220px',
            marginRight: 0,
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: DarkGreyOutline,
              borderWidth: '1px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: DarkGreyOutline,
              borderWidth: '1px',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: DarkGreyOutline,
              borderWidth: '1px',
            },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: '#e53935',
              borderWidth: '1px',
            },
          }}
          value={value ?? []}
        >
          {options.map(option => (
            <StyledMenuItem key={option.id} value={option.id}>
              <StyledCheckbox
                checked={value?.includes(option.id)}
                size="small"
              />
              <ListItemText primary={option.name} />
            </StyledMenuItem>
          ))}
        </Select>
        {error ? <ValidationError error={meta.error ?? ''} /> : null}{' '}
      </StyledFormControl>
    </FormGroup>
  );
};
